import type { ResolvedInstallationOrder } from "../types/installation";
import {
  inferInstallationDiscountScopeFromResolved,
  splitInstallationDiscountForPayload,
} from "./installationDiscountScope";
import { round2 } from "utils/fixedAmountDiscountAllocation";

const lineKey = (productId: unknown, variantId: unknown) =>
  `${String(productId ?? "")}:${String(variantId ?? "")}`;

/** Merge per-line tax/discount/subtotals from resolve into existing line_items. */
export function mergeResolvedTaxIntoLineItems(
  existingLineItems: unknown[],
  resolvedLineItems: unknown[] | null | undefined
): unknown[] {
  const resolvedByKey = new Map(
    (resolvedLineItems || []).map((raw) => {
      const li = raw as Record<string, unknown>;
      return [lineKey(li.product_id, li.variant_id), li];
    })
  );

  return (existingLineItems || []).map((raw) => {
    const li = { ...(raw as Record<string, unknown>) };
    if (li.is_new_purchase !== true) {
      return {
        ...li,
        tax_amount: 0,
        item_tax_info: {
          ...(typeof li.item_tax_info === "object" && li.item_tax_info
            ? (li.item_tax_info as object)
            : {}),
          tax_percentage: 0,
          unit_product_tax_amount: 0,
          total_product_tax_amount: 0,
        },
      };
    }

    const r = resolvedByKey.get(lineKey(li.product_id, li.variant_id)) as
      | Record<string, unknown>
      | undefined;
    if (!r) return li;

    const taxInfo =
      (r.item_tax_info as Record<string, unknown> | undefined) ??
      (li.item_tax_info as Record<string, unknown> | undefined);
    const lineTax = Number(
      (taxInfo as { total_product_tax_amount?: number } | undefined)
        ?.total_product_tax_amount ?? li.tax_amount ?? 0
    );
    const rowDiscTotal = round2(
      Number(
        r.discount_on_total_quantity ??
          (r as { discount?: number }).discount ??
          li.discount_on_total_quantity ??
          li.discount ??
          0
      )
    );
    const qty = Math.max(1, Number(li.quantity) || 1);
    const perUnitDisc =
      r.discount_on_unit_quantity != null
        ? round2(Number(r.discount_on_unit_quantity))
        : rowDiscTotal > 0
          ? round2(rowDiscTotal / qty)
          : round2(Number(li.discount_on_unit_quantity ?? 0));

    return {
      ...li,
      unit_price: r.unit_price ?? li.unit_price,
      unit_retail_price: r.unit_retail_price ?? r.unit_price ?? li.unit_retail_price ?? li.unit_price,
      changed_unit_price: r.changed_unit_price ?? li.changed_unit_price,
      is_price_overridden:
        typeof r.is_price_overridden === "boolean"
          ? r.is_price_overridden
          : li.is_price_overridden,
      item_sub_total: r.item_sub_total ?? li.item_sub_total,
      total_price: r.total_price ?? li.total_price,
      tax_amount: lineTax,
      item_tax_info: taxInfo ?? li.item_tax_info,
      discount: rowDiscTotal,
      discount_on_unit_quantity: perUnitDisc,
      discount_on_total_quantity: rowDiscTotal,
      discount_code: String(r.discount_code ?? li.discount_code ?? "").trim(),
    };
  });
}

/** PATCH body for signage live-tax / edit save from last resolve response. */
export function buildInstallationPatchFromResolved(
  resolved: ResolvedInstallationOrder,
  args: {
    version?: number | string | null;
    installationCost: number;
    existingLineItems: unknown[];
    taxStatus?: string;
  }
): Record<string, unknown> {
  const feeRows = (resolved.specialfee || []).map((f) => ({
    specialfee_id: String(f.specialfee_id || ""),
    specialfee_name: String(f.specialfee_name || ""),
    specialfee_amount: Number(f.specialfee_amount || 0),
    specialfee_tax_percentage: Number(f.specialfee_tax_percentage || 0),
    specialfee_tax_amount: Number(f.specialfee_tax_amount || 0),
    specialfee_taxable: f.specialfee_taxable !== false,
  }));

  const installTax = Number(resolved.installation_tax_amount || 0);
  const installTaxPct = String(resolved.installation_tax_percentage ?? "");
  const overallTax = Number(resolved.over_all_tax_amount || 0);
  const overallTaxPct = String(resolved.over_all_tax_percentage ?? "");
  const subtotalAmount = Number(resolved.subtotal_amount ?? 0);
  const totalAmount = Number(resolved.total_amount ?? 0);
  const scope = inferInstallationDiscountScopeFromResolved(resolved);
  const { rootDiscountCode } = splitInstallationDiscountForPayload(
    String(resolved.discount_code ?? ""),
    scope
  );
  const orderDiscountAmount = Number(resolved.discount_amount ?? 0);

  return {
    version: args.version,
    subtotal_amount: subtotalAmount,
    total_amount: totalAmount,
    installation_cost: Number(resolved.installation_cost ?? args.installationCost),
    installation_tax_amount: installTax,
    installationtaxamount: installTax,
    installationtaxpercentage: installTaxPct,
    installation_tax_percentage: installTaxPct,
    specialfee: feeRows,
    line_items: mergeResolvedTaxIntoLineItems(args.existingLineItems, resolved.line_items),
    tax_status: args.taxStatus ?? resolved.tax_status ?? "draft",
    over_all_tax_amount: overallTax,
    over_all_tax_percentage: overallTaxPct,
    discount_amount: scope === "installation" ? orderDiscountAmount : 0,
    discount_code: scope === "installation" ? rootDiscountCode : "",
    discount_applies_to: scope,
  };
}
